import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { INHERITANCE_IRA } from '@shared/helpers';
import { AccountTypes, FlowTypes, urlMapper } from '@shared/models';
import * as fromRootStore from '@store/index';
import { External } from '@store/index';
import { InternalTransferTriage } from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { selectInternalTransferTriage } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { selectRouterState } from '@store/selectors/router/common/router.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, Observable, of, Subscription, switchMap, tap, withLatestFrom } from 'rxjs';

@Injectable()
export class LaunchGuard implements OnDestroy {
  subs: Subscription = new Subscription();
  validFlowType = [FlowTypes.INHERITANCE, FlowTypes.GIFTING, FlowTypes.MINOR];
  overviewPage!: string;

  constructor(
    private globalStore: Store<fromRootStore.State>,
    private cookieService: CookieService,
    private location: Location,
  ) {}

  canActivate(): Observable<boolean> {
    return this.cookieService.get('routeGuard') === 'OFF'
      ? of(true)
      : this.checkStore().pipe(
          switchMap((canActivate) => {
            if (canActivate) {
              return of(true);
            } else {
              const url = this.location.prepareExternalUrl(`/${this.overviewPage}`);
              this.globalStore.dispatch(External({ url }));
              return of(false);
            }
          }),
          catchError(() => of(false)),
        );
  }

  // if the query params do NOT contain a valid account type or the required keys
  // AND guide state does not contain account type inheriting selection
  // the user will be redirected to the overview page based on experimentation split
  checkStore(): Observable<any> {
    return this.globalStore.pipe(
      withLatestFrom(
        this.globalStore.select(selectRouterState),
        this.globalStore.select(selectTriageState),
        this.globalStore.select(selectInternalTransferTriage),
      ),
      switchMap(([, router, triageState, internalTransferTriageState]) => {
        const isValid = this.hasValidStateParams(triageState, internalTransferTriageState);
        if (!isValid) {
          this.overviewPage = overviewRedirect(router.url);
          return of(false);
        } else {
          return of(true);
        }
      }),
    );
  }

  enumIncludes(e: any, value: string) {
    // excluding empty string
    return Object.values(e)
      .filter((e) => e !== '')
      .includes(value);
  }

  hasValidStateParams(
    triageState: TriageState,
    internalTransferTriageState: InternalTransferTriage,
  ): boolean {
    return (
      this.enumIncludes(FlowTypes, triageState.flowType) &&
      (triageState.flowType === FlowTypes.MULTI_INHERITANCE ||
        triageState.flowType === FlowTypes.INHERITED_IRA ||
        this.enumIncludes(
          AccountTypes,
          triageState.accountTypeInheriting || internalTransferTriageState.transferFrom,
        ))
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}

export function overviewRedirect(url: string): string {
  let currentFlow: FlowTypes;

  switch (true) {
    case urlMapper.MINOR && url.includes(urlMapper.MINOR):
      currentFlow = FlowTypes.MINOR;
      break;
    case urlMapper.GIFTING && url.includes(urlMapper.GIFTING):
      currentFlow = FlowTypes.GIFTING;
      break;
    case urlMapper.INTERNAL_TRANSFER && url.includes(urlMapper.INTERNAL_TRANSFER):
      currentFlow = FlowTypes.INTERNAL_TRANSFER;
      break;
    case urlMapper.MULTI_INHERITANCE && url.includes(urlMapper.MULTI_INHERITANCE):
      currentFlow = FlowTypes.MULTI_INHERITANCE;
      break;
    case urlMapper.INHERITED_IRA && url.includes(urlMapper.INHERITED_IRA):
      currentFlow = FlowTypes.INHERITED_IRA;
      break;
    case urlMapper.INHERITANCE && url.includes(urlMapper.INHERITANCE):
    default:
      currentFlow = FlowTypes.INHERITANCE;
      break;
  }

  const overviewPage: { [key: string]: string } = {
    INHERITANCE: 'inheritance/overview',
    MULTI_INHERITANCE: 'inheritance/overview',
    MINOR: 'minor-transfer/overview',
    GIFTING: 'gifting/overview',
    INTERNAL_TRANSFER: 'internal-transfer/overview',
    INHERITED_IRA: 'inherited-ira/triage',
  };

  return overviewPage[currentFlow];
}
